//#region Imports

import { IPageConfig } from "../../interfaces/page_config";
import { FormKey, LocalizedStrings } from "../../types";
import { DEFAULT_FORM_LABELS } from "./form_labels_defaults";

//#endregion

//#region Label Resolver

/**
 * Type guard for localized values (strings or string arrays)
 */
const isLocalizedValue = (v: unknown): v is string | string[] => {
  return typeof v === "string" || (Array.isArray(v) && v.every((x) => typeof x === "string"));
};

/**
 * Sanitize an object to only include valid localized strings or string arrays
 */
const sanitizeLabels = (obj: unknown): LocalizedStrings => {
  try {
    if (!obj || typeof obj !== "object") return {};
    const out: LocalizedStrings = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (isLocalizedValue(v)) out[k] = v as any;
    }
    return out;
  } catch (err) {
    //console.error("[label_resolver] sanitizeLabels error:", err);
    return {};
  }
};

/**
 * Merge labels for the given form with English defaults.
 * Filters out invalid entries and falls back to defaults when missing.
 */
export function resolveFormLabels(pageConfig: IPageConfig | null | undefined, formKey: FormKey): LocalizedStrings {
  const defaults = DEFAULT_FORM_LABELS[formKey] ?? {};

  try {
    const labelsFromState = pageConfig?.formLabels?.[formKey];

    if (!labelsFromState || typeof labelsFromState !== "object") {
      return { ...defaults };
    }

    const clean = sanitizeLabels(labelsFromState);
    return { ...defaults, ...clean };
  } catch (err) {
    // fall back to defaults
    console.error("[label_resolver] resolveFormLabels error:", err);
    return { ...defaults };
  }
}

/**
 * Convenience resolver for send_transcript_form
 */
export function resolveSendTranscriptLabels(pageConfig: IPageConfig | null | undefined): LocalizedStrings {
  return resolveFormLabels(pageConfig, "send_transcript_form");
}

/**
 * Convenience resolver for feedback_form
 */
export function resolveFeedbackFormLabels(pageConfig: IPageConfig | null | undefined): LocalizedStrings {
	return resolveFormLabels(pageConfig, "feedback_form");
}

/**
 * This is a convenience resolver for language_selector tooltip
 */
export function resolveLanguageSelectorLabels(pageConfig: IPageConfig | null | undefined): LocalizedStrings {
  return resolveFormLabels(pageConfig, "language_selector");
}

/**
 * This is a convenience resolver for info_button tooltip
 */
export function resolveInfoButtonLabels(pageConfig: IPageConfig | null | undefined): LocalizedStrings {
  return resolveFormLabels(pageConfig, "info_button");
}

/**
 * Main resolver for a specific send_transcript_form label by key.
 * Falls back to the default label if not found in the resolved labels.
 * @param key - The label key to look up
 * @param pageConfig - Optional page configuration
 * @returns The resolved label string for the given key, or the default, or empty string
 */
export function resolveSendTranscriptLabel(key: string, pageConfig: IPageConfig | null | undefined): string {
  try {
    const labels = resolveSendTranscriptLabels(pageConfig);
    const v = (labels as LocalizedStrings)[key];
    return typeof v === "string" ? v : DEFAULT_FORM_LABELS.send_transcript_form[key] || "";
  } catch {
    return DEFAULT_FORM_LABELS.send_transcript_form[key] || "";
  }
}

/**
 * Main resolver for a specific feedback_form label by key.
 * Falls back to the default label if not found in the resolved labels.
 * Can return either a string or string array (e.g., for quick_replies).
 * @param key - The label key to look up
 * @param pageConfig - Optional page configuration
 * @returns The resolved label (string or string array) for the given key, or the default, or empty string
 */
export function resolveFeedbackFormLabel(key: string, pageConfig: IPageConfig | null | undefined): string | string[] {
  try {
    const labels = resolveFeedbackFormLabels(pageConfig);
    const v = (labels as LocalizedStrings)[key];
    if (isLocalizedValue(v)) return v;

    // Fallback to default
    const defaultValue = (DEFAULT_FORM_LABELS.feedback_form as LocalizedStrings)[key];
    return defaultValue || "";
  } catch {
    const defaultValue = (DEFAULT_FORM_LABELS.feedback_form as LocalizedStrings)[key];
    return defaultValue || "";
  }
}

/**
 * Helper resolver for a specific form label by key and formKey.
 */
function resolveStringLabel(key: string, pageConfig: IPageConfig | null | undefined, formKey: FormKey): string {
  try {
    const labels = resolveFormLabels(pageConfig, formKey);
    const v = labels[key];
    return typeof v === "string" ? v : DEFAULT_FORM_LABELS[formKey]?.[key] || "";
  } catch {
    return DEFAULT_FORM_LABELS[formKey]?.[key] || "";
  }
}

/**
 * Main resolver for a specific language_selector tooltip by key.
 */
export function resolveLanguageSelectorLabel(key: string, pageConfig: IPageConfig | null | undefined): string {
  return resolveStringLabel(key, pageConfig, "language_selector");
}

/**
 * Main resolver for a specific info_button tooltip by key.
 */
export function resolveInfoButtonLabel(key: string, pageConfig: IPageConfig | null | undefined): string {
  return resolveStringLabel(key, pageConfig, "info_button");
}

/**
 * Direct accessors for default Send Transcript labels (without pageConfig)
 * @param key - The label key to look up
 * @returns The default label string for the given key, or empty string
 */
export function getDefaultSendTranscriptLabel(key: string): string {
  try {
    return DEFAULT_FORM_LABELS.send_transcript_form[key] || "";
  } catch {
    return key;
  }
}

/**
 * Direct accessors for default Feedback Form labels (without pageConfig)
 * @param key - The label key to look up
 * @returns The default label (string or string array) for the given key, or empty string
 */
export function getDefaultFeedbackFormLabel(key: string): string | string[] {
  try {
    return DEFAULT_FORM_LABELS.feedback_form[key] || "";
  } catch {
    return key;
  }
}

/**
 * Direct accessors for default Language Selector labels (without pageConfig)
 */
export function getDefaultLanguageSelectorLabel(key: string): string {
  return DEFAULT_FORM_LABELS.language_selector?.[key] || "";
}

/**
 * Direct accessors for default Info Button labels (without pageConfig)
 */
export function getDefaultInfoButtonLabel(key: string): string {
  return DEFAULT_FORM_LABELS.info_button?.[key] || "";
}

//#endregion
